import React, { useState, useEffect } from 'react';
import revenueApi from '../api/revenue';
import RevenueCard from './RevenueCard';
import AlertMessage from './AlertMessage';
import { useSettings } from '../context/SettingsContext';

/**
 * Revenue Overview Dashboard Component
 * Displays comprehensive revenue statistics and charts
 * Supports toggle between "Actual Revenue" (collected) and "Expected Revenue" (booked)
 */
export default function RevenueOverview() {
  const { settings } = useSettings();
  const cs = settings.currency_symbol || '$';
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [viewMode, setViewMode] = useState('collected'); // 'collected' or 'booked'

  useEffect(() => {
    const timer = setTimeout(() => {
      loadStats();
    }, 500);

    return () => clearTimeout(timer);
  }, [retryCount]);

  const loadStats = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { data } = await revenueApi.getRevenueStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load revenue stats:', err);

      const status = err.response?.status;
      if (retryCount < 2 && status !== 403 && status !== 404) {
        setTimeout(() => setRetryCount(prev => prev + 1), 1500);
      } else {
        setError(err.response?.data?.message || 'Failed to load revenue statistics');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-slate-200 p-6 dark:border-slate-800 animate-pulse"
            >
              <div className="h-4 w-24 bg-slate-200 rounded dark:bg-slate-700"></div>
              <div className="h-8 w-32 bg-slate-200 rounded mt-2 dark:bg-slate-700"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <AlertMessage type="error">{error}</AlertMessage>;
  }

  if (!stats) {
    return <div>No revenue data available</div>;
  }

  // Dynamic revenue field based on view mode
  const getRevenue = (item) => viewMode === 'collected' ? (item.collected ?? item.revenue) : (item.booked ?? item.revenue);

  // Get max value for chart scaling
  const maxRevenue = Math.max(...stats.revenue_by_day.map(d => getRevenue(d)), 1);

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setViewMode('collected')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'collected'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
          }`}
        >
          Actual Revenue
        </button>
        <button
          onClick={() => setViewMode('booked')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'booked'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
          }`}
        >
          Expected Revenue
        </button>
      </div>

      {/* Revenue Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <RevenueCard
          title={viewMode === 'collected' ? "Collected Revenue" : "Total Revenue"}
          amount={viewMode === 'collected' ? stats.collected_revenue : stats.total_revenue}
          subtitle={`${stats.total_bookings} completed bookings`}
          icon="💰"
        />
        <RevenueCard
          title="This Month"
          amount={viewMode === 'collected' ? stats.collected_this_month : stats.this_month}
          trend={viewMode === 'collected' ? stats.collected_monthly_change : stats.monthly_change}
          trendDirection={viewMode === 'collected' ? stats.collected_monthly_trend : stats.monthly_trend}
          icon="📊"
        />
        <RevenueCard
          title="This Week"
          amount={viewMode === 'collected' ? stats.collected_this_week : stats.this_week}
          trend={viewMode === 'collected' ? stats.collected_weekly_change : stats.weekly_change}
          trendDirection={viewMode === 'collected' ? stats.collected_weekly_trend : stats.weekly_trend}
          icon="📈"
        />
        <RevenueCard
          title="Average Booking"
          amount={viewMode === 'collected' ? stats.avg_collected : stats.avg_booking}
          subtitle="per transaction"
          icon="💵"
        />
        <RevenueCard
          title="Outstanding"
          amount={stats.outstanding_amount}
          subtitle="remaining balance"
          icon="⏳"
        />
      </div>

      {/* Revenue Chart - Last 30 Days */}
      <div className="rounded-xl border border-emerald-200/50 p-6 dark:border-emerald-800/50 bg-gradient-to-br from-emerald-50/30 via-white to-teal-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900/20 shadow-lg overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <span className="text-3xl">📈</span>
            <div>
              <span className="block">Revenue Trend</span>
              <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                {viewMode === 'collected' ? 'Actual collected revenue' : 'Expected booked revenue'}
              </span>
            </div>
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-4 py-2 rounded-full border border-emerald-200 dark:border-emerald-800">
              Last 30 Days
            </span>
          </div>
        </div>
        <div className="relative">
          {/* Grid lines with labels */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pr-4">
            {[100, 75, 50, 25, 0].map((percent, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-400 dark:text-slate-500 min-w-[60px] text-right">
                  {cs}{((maxRevenue * percent) / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </span>
                <div className="flex-1 border-b border-dashed border-emerald-200/40 dark:border-emerald-700/30"></div>
              </div>
            ))}
          </div>

          {/* Bars */}
          <div className="flex items-end justify-between gap-1.5 h-80 pl-20 pr-4 pt-2 pb-4 relative">
            {stats.revenue_by_day.map((day, index) => {
              const revenueValue = getRevenue(day);
              const heightPercent = (revenueValue / maxRevenue) * 100;
              const isToday = new Date(day.date).toDateString() === new Date().toDateString();

              return (
                <div key={index} className="flex-1 flex flex-col justify-end items-center gap-2 group relative">
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full mb-4 opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-95 transition-all duration-200 bg-gradient-to-br from-slate-900 to-slate-800 dark:from-white dark:to-slate-50 text-white dark:text-slate-900 text-xs rounded-xl px-5 py-4 whitespace-nowrap z-20 shadow-2xl border-2 border-emerald-500/30 dark:border-emerald-600/30">
                    <div className="font-bold text-base mb-2 text-emerald-400 dark:text-emerald-600">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                    <div className="text-white dark:text-slate-900 font-bold text-xl mb-1">
                      {cs}{revenueValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-slate-300 dark:text-slate-600 text-xs flex items-center gap-1">
                      <span>📦</span>
                      <span>{day.bookings} {day.bookings === 1 ? 'booking' : 'bookings'}</span>
                    </div>
                    {/* Arrow */}
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-3 h-3 rotate-45 bg-slate-900 dark:bg-white border-r-2 border-b-2 border-emerald-500/30 dark:border-emerald-600/30"></div>
                  </div>

                  {/* Bar with animation */}
                  <div className="w-full relative group/bar">
                    <div
                      className={`w-full rounded-t-xl shadow-md group-hover/bar:shadow-xl transition-all duration-300 ease-out cursor-pointer relative overflow-hidden ${
                        isToday
                          ? 'bg-gradient-to-t from-yellow-600 via-yellow-500 to-yellow-400 dark:from-yellow-500 dark:via-yellow-400 dark:to-yellow-300 group-hover/bar:from-yellow-700 group-hover/bar:via-yellow-600 group-hover/bar:to-yellow-500'
                          : 'bg-gradient-to-t from-emerald-600 via-emerald-500 to-emerald-400 dark:from-emerald-600 dark:via-emerald-500 dark:to-emerald-400 group-hover/bar:from-emerald-700 group-hover/bar:via-emerald-600 group-hover/bar:to-emerald-500 dark:group-hover/bar:from-emerald-700 dark:group-hover/bar:via-emerald-600 dark:group-hover/bar:to-emerald-500'
                      }`}
                      style={{
                        height: `${heightPercent}%`,
                        minHeight: revenueValue > 0 ? '8px' : '2px',
                      }}
                    >
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/20 to-white/40 opacity-0 group-hover/bar:opacity-100 transition-opacity duration-300"></div>

                      {/* Pulse animation for bars with revenue */}
                      {revenueValue > 0 && (
                        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover/bar:opacity-100 group-hover/bar:animate-pulse"></div>
                      )}
                    </div>
                  </div>

                  {/* Date label (show every 3 days or today) */}
                  {(index % 3 === 0 || isToday) && (
                    <span className={`text-xs font-semibold ${
                      isToday
                        ? 'text-yellow-600 dark:text-yellow-400 font-bold'
                        : 'text-slate-500 dark:text-slate-400'
                    }`}>
                      {isToday ? 'Today' : new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).replace(' ', '\n')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-slate-200/50 dark:border-slate-700/50 flex items-center justify-center gap-6 text-xs">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-400 shadow-sm"></div>
            <span className="font-medium text-slate-700 dark:text-slate-300">Regular Day</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-50 dark:bg-yellow-900/20">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-yellow-600 to-yellow-400 shadow-sm"></div>
            <span className="font-medium text-slate-700 dark:text-slate-300">Today</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 dark:bg-slate-800">
            <span className="text-slate-600 dark:text-slate-400">💡</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">Hover for details</span>
          </div>
        </div>
      </div>

      {/* Revenue by City & Best Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by City */}
        <div className="rounded-xl border border-slate-200 p-6 dark:border-slate-800 bg-white dark:bg-slate-900">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <span>🌍</span>
            <span>Revenue by City</span>
          </h3>
          <div className="space-y-3">
            {stats.revenue_by_city.map((city, index) => {
              const cityRevenue = getRevenue(city);
              const topCityRevenue = getRevenue(stats.revenue_by_city[0]);
              return (
                <div key={city.city} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        #{index + 1}
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {city.city}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                        {cs}{cityRevenue.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {city.bookings} bookings
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2.5 dark:bg-slate-700">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
                      style={{
                        width: `${topCityRevenue > 0 ? (cityRevenue / topCityRevenue) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Best Performers */}
        <div className="space-y-4">
          {stats.best_city && (
            <div className="rounded-xl border-2 border-emerald-200 p-6 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-3xl">🏆</span>
                <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">Best Performing City</h4>
              </div>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                {stats.best_city.city}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {cs}{getRevenue(stats.best_city).toLocaleString()} revenue • {stats.best_city.bookings} bookings
              </p>
            </div>
          )}

          {stats.best_tour && (
            <div className="rounded-xl border-2 border-blue-200 p-6 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-900">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-3xl">⭐</span>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">Top Revenue Tour</h4>
              </div>
              <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                {stats.best_tour.title}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {cs}{getRevenue(stats.best_tour).toLocaleString()} revenue • {stats.best_tour.bookings} bookings
              </p>
            </div>
          )}

          {/* Outstanding Summary */}
          {stats.outstanding_amount > 0 && (
            <div className="rounded-xl border-2 border-amber-200 p-6 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-900">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-3xl">⏳</span>
                <h4 className="font-semibold text-amber-900 dark:text-amber-100">Outstanding Balance</h4>
              </div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {cs}{stats.outstanding_amount.toLocaleString()}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Remaining balance to be collected from active bookings
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
